import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "@/i18n";

const HERO_IMG = "/images/hero-galerie-du-roi.jpg";

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const stats = [
    { value: "FR·EN·NL", label: t("hero.stat1") },
    { value: "360°", label: t("hero.stat2") },
    { value: "5 min", label: t("hero.stat3") },
  ];

  const scrollTo = (href) => {
    const target = document.querySelector(href);
    if (target && window.__lenis) window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  };

  return (
    <section
      id="accueil"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt={t("hero.imgAlt")}
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/40 to-ink" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-[1400px] mx-auto w-full px-6 sm:px-12 lg:px-20 pt-40 pb-16"
      >
        <RevealLine delay={0.35}>
          <span className="label-accent" data-testid="hero-overline">
            {t("hero.overline")}
          </span>
        </RevealLine>

        <h1 className="mt-8 font-serif font-light leading-[1.05] text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
          <RevealLine delay={0.5}>{t("hero.title1")}</RevealLine>
          <RevealLine delay={0.65}>
            {t("hero.title2a")}<em className="text-gold-gradient not-italic font-normal">{t("hero.title2em")}</em>{t("hero.title2b")}
          </RevealLine>
          <RevealLine delay={0.8}>
            <em className="italic font-light text-[#A0A5B1]">{t("hero.title3")}</em>
          </RevealLine>
        </h1>

        <div className="mt-10 max-w-xl overflow-hidden">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-base sm:text-lg text-[#A0A5B1] leading-relaxed font-light"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            onClick={() => scrollTo("#contact")}
            data-testid="hero-cta-consulter"
            className="group relative bg-gold text-ink text-xs uppercase tracking-[0.25em] px-8 py-4 font-medium overflow-hidden"
          >
            <span className="relative z-10">{t("hero.cta1")}</span>
            <span className="absolute inset-0 bg-gold-light translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
          </button>
          <button
            onClick={() => scrollTo("#expertises")}
            data-testid="hero-cta-expertises"
            className="border border-gold/40 text-gold text-xs uppercase tracking-[0.25em] px-8 py-4 hover:border-gold hover:bg-gold/10 transition-colors duration-300"
          >
            {t("hero.cta2")}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="mt-20 pt-8 border-t border-gold/15 grid grid-cols-1 sm:grid-cols-3 gap-8"
          data-testid="hero-stats"
        >
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.7 + i * 0.15 }}
              className="flex flex-col gap-1"
            >
              <span className="font-mono text-3xl sm:text-4xl text-gold-gradient font-light" data-testid={`hero-stat-value-${i}`}>
                {s.value}
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-[#9BA1B0]">{s.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
